import { useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { TokenPayload } from "../types/ComponentProps";

/**
 * Custom hook to determine if the current user can give coins.
 * A user can give coins if:
 * 1. They are an admin (isAdmin = 1), OR
 * 2. Their user ID is in the REACT_APP_COIN_GIVER_IDS environment variable
 *
 * @returns boolean indicating if the user can give coins
 */
const useCanGiveCoins = (): boolean => {
  const canGiveCoins = useMemo(() => {
    const accessToken = localStorage.getItem("accessTokenGC");
    
    if (!accessToken) {
      return false;
    }

    try {
      const userDetails: TokenPayload = jwtDecode<TokenPayload>(accessToken);

      // Admins can always give coins
      if (userDetails?.isAdmin) {
        return true;
      }

      // Check if user ID is in the allowed coin giver list
      const allowedIds = (process.env.REACT_APP_COIN_GIVER_IDS || "")
        .split(",")
        .map((id) => Number(id.trim()))
        .filter((id) => !isNaN(id) && id > 0);

      if (userDetails?.id && allowedIds.includes(userDetails.id)) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error decoding token in useCanGiveCoins:", error);
      return false;
    }
  }, []);

  return canGiveCoins;
};

export default useCanGiveCoins;
